import { ReactElement, useEffect } from "react";
import { Outlet } from "react-router-dom";

import { Message } from "@sabre/spark-react-core";
import { MessageRole, MessageStatus, MessageType } from "@sabre/spark-react-core/types";

import classNames from "classnames";

import FooterAIQ from "@components/common/FooterAIQ";
import HeaderAIQ from "@components/common/HeaderAIQ";
import Toaster from "@components/toaster";
import { UnsavedConfirmationModal } from "@components/unsaved-confirmation-modal";

import { useStore } from "@store/Store";
import { overlaySelector } from "@store/slices/overview/OverviewSelectors";

import useOnlineStatus from "@utils/hooks/useOnlineStatus";

const Layout = (): ReactElement => {
  const isOnline = useOnlineStatus();

  const overlay = useStore(overlaySelector);
  const { isOverlayOpen, bodyElement } = overlay;

  useEffect(() => {
    if (isOverlayOpen) {
      bodyElement.classList.add("aiq-overlay-open");
    } else {
      bodyElement.classList.remove("aiq-overlay-open");
    }

    return (): void => {
      bodyElement.classList.remove("aiq-overlay-open");
    };
  }, [bodyElement.classList, isOverlayOpen]);

  return (
    <div className="spark-content__wrapper--sticky-footer">
      <HeaderAIQ />

      <div id="overlay-root"></div>
      <main className="spark-main--sticky-footer">
        <div className="container-fluid spark-pad-l-2 spark-pad-r-2">
          {!isOnline && (
            <Message
              className={"spark-mar-t-2 spark-mar-b-2"}
              title="You're currently offline"
              content="Please ensure you have a stable internet connection to access all features of this application. Some features may be limited or unavailable while offline."
              status={MessageStatus.WARNING}
              type={MessageType.PAGE}
              animate={true}
              role={MessageRole.ALERT}
            />
          )}
          <div
            className={classNames("spark-mar-t-1", {
              "aiq-app-offline": !isOnline,
            })}
          >
            <Outlet data-testid="layout-outlet" />
          </div>
        </div>
      </main>

      <Toaster />
      <UnsavedConfirmationModal />
      <FooterAIQ />
    </div>
  );
};

export default Layout;
